import { PromotionProvider } from "./context/PromotionContext";
import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  useParams,
  Navigate,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Products from "./pages/Products";
import Cart from "./pages/Cart";
import OrderSummary from "./components/OrderSummary";
import OrderConfirmation from "./components/OrderConfirmation";
import PaymentProcessing from "./components/PaymentProcessing";
import PaymentSuccess from "./components/PaymentSuccess";
import PaymentFailed from "./components/PaymentFailed";
import AdditionalPaymentRedirect from "./components/AdditionalPaymentRedirect";
import PaymentRequisites from "./pages/PaymentRequisites";
import Login from "./pages/Login";
import Register from "./pages/Register";
import CustomerServiceInfo from "./pages/CustomerServiceInfo";
import BonusProgram from "./pages/BonusProgram";
import { LoyaltySettingsProvider, LoyaltyVisibleRoute } from "./context/LoyaltySettingsContext";
import ProductDetail from "./pages/ProductDetail";
import GuestWishlist from "./pages/GuestWishlist";
import Comparison from "./pages/Comparison";

import Profile from "./pages/UserCabinet/ProfileSection";
import Orders from "./pages/UserCabinet/OrdersSection";
import Addresses from "./pages/UserCabinet/AddressesSection";
import Wishlist from "./pages/UserCabinet/WishlistSection";
import Subscriptions from "./pages/UserCabinet/SubscriptionsSection";
import Returns from "./pages/UserCabinet/ReturnsSection";
import Loyalty from "./pages/UserCabinet/LoyaltySection";
import UserCabinet from "./pages/UserCabinet/UserCabinet";

import ResetPassword from "./components/ResetPassword";
import ScrollToTop from "./components/ScrollToTop";

import { CustomerProvider, useCustomer } from "./context/CustomerContext";
import { AuthProvider } from "./context/AuthContext";
import { OrderProvider } from "./context/OrderContext";
import { CartProvider } from "./context/CartContext";
import { WishlistProvider } from "./context/WishlistContext";
import { ComparisonProvider } from "./context/ComparisonContext";
import { DeliverySettingsProvider } from "./context/DeliverySettingsContext";
import { SEOProvider } from "./context/SEOContext";
import SEOManager from "./components/SEOManager";

import { Analytics } from "@vercel/analytics/react";
import ReactGA from "react-ga4";

import Blocked from "./pages/Blocked";
import styles from "./App.module.css";

// ✅ Google Analytics
ReactGA.initialize(process.env.REACT_APP_GA_ID || "G-XXXXXXXXXX");

function GAListener() {
  const location = useLocation();
  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: location.pathname + location.search,
    });
  }, [location]);
  return null;
}

function ProductsWrapper() {
  const location = useLocation();
  return <Products key={location.pathname} />;
}

function LegacyCategoryRedirect() {
  const { categorySlug } = useParams();
  return <Navigate to={`/category/${categorySlug}`} replace />;
}

function CustomerServiceRedirect() {
  const location = useLocation();
  return <Navigate to={`/customer-service-info${location.hash || ""}`} replace />;
}

function CustomerProtectedRoute({ children }) {
  const { customer, loading } = useCustomer();

  if (loading) {
    return <div>Завантаження...</div>;
  }

  if (!customer) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppContent() {
  const location = useLocation();

  return (
    <div className={styles.appContainer}>
      <Header />
      <SEOManager key={location.pathname + location.search} />

      <main className={styles.mainContent}>
        <div className={styles.pageContainer}>
          <Routes>
            {/* Публічні сторінки */}
            <Route path="/" element={<Home />} />
            <Route path="/products" element={<ProductsWrapper />} />
            <Route path="/cats" element={<Navigate to="/category/cats" replace />} />
            <Route path="/dogs" element={<Navigate to="/category/dogs" replace />} />
            <Route path="/birds" element={<Navigate to="/category/birds" replace />} />
            <Route path="/rodents" element={<Navigate to="/category/rodents" replace />} />
            <Route path="/fish" element={<Navigate to="/category/fish" replace />} />
            <Route path="/reptiles" element={<Navigate to="/category/reptiles" replace />} />
            <Route
              path="/category/:categorySlug"
              element={<ProductsWrapper />}
            />
            <Route
              path="/category/:categorySlug/:kindSlug"
              element={<ProductsWrapper />}
            />
            <Route path="/brands/:brandSlug" element={<ProductsWrapper />} />
            <Route path="/promotions" element={<ProductsWrapper />} />
            <Route
              path="/promotions/category/:categorySlug"
              element={<ProductsWrapper />}
            />
            <Route
              path="/promotions/category/:categorySlug/:kindSlug"
              element={<ProductsWrapper />}
            />
            <Route path="/sale" element={<Navigate to="/promotions" replace />} />
            <Route
              path="/products/category/:categorySlug"
              element={<LegacyCategoryRedirect />}
            />
            <Route
              path="/category/:categorySlug/:kindSlug/:brandSlug/:slug"
              element={<ProductDetail />}
            />
            <Route path="/products/:slug" element={<ProductDetail />} />
            <Route path="/product/code/:code" element={<ProductDetail />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/wishlist" element={<GuestWishlist />} />
            <Route path="/comparison" element={<Comparison />} />
            <Route path="/customer-service-info" element={<CustomerServiceInfo />} />
            <Route path="/bonus-program" element={<LoyaltyVisibleRoute><BonusProgram /></LoyaltyVisibleRoute>} />
            <Route path="/delivery-info" element={<CustomerServiceRedirect />} />
            <Route path="/delivery-payment" element={<CustomerServiceRedirect />} />
            <Route path="/checkout" element={<Navigate to="/cart" replace />} />
            <Route path="/order-summary" element={<OrderSummary />} />
            <Route path="/order-confirmation" element={<OrderConfirmation />} />
            <Route path="/payment-requisites" element={<PaymentRequisites />} />
            <Route path="/payment/processing" element={<PaymentProcessing />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/failed" element={<PaymentFailed />} />
            <Route
              path="/pay/additional/:orderId/:secureToken"
              element={<AdditionalPaymentRedirect />}
            />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Кабінет користувача */}
            <Route
              path="/cabinet/*"
              element={
                <CustomerProtectedRoute>
                  <UserCabinet />
                </CustomerProtectedRoute>
              }
            >
              <Route path="profile" element={<Profile />} />
              <Route path="orders" element={<Orders />} />
              <Route path="addresses" element={<Addresses />} />
              <Route path="wishlist" element={<Wishlist />} />
              <Route path="subscriptions" element={<Subscriptions />} />
              <Route path="returns" element={<Returns />} />
              <Route path="loyalty" element={<LoyaltyVisibleRoute redirectTo="/cabinet/profile"><Loyalty /></LoyaltyVisibleRoute>} />
              <Route index element={<Profile />} />
            </Route>

            {/* 404 */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </main>
      <Footer />
      <Analytics />
    </div>
  );
}

export default function App() {
  const [allowed, setAllowed] = useState(null);

  useEffect(() => {
    async function checkAccess() {
      if (window.location.hostname === "localhost") {
        setAllowed(true);
        return;
      }

      try {
        const res = await fetch("/api/check-ip");
        const data = await res.json();

        if (res.ok && data.allowed) {
          setAllowed(true);
        } else {
          setAllowed(false);
        }
      } catch (err) {
        console.error("❌ Помилка перевірки доступу:", err);
        setAllowed(false);
      }
    }

    checkAccess();
  }, []);

  if (!allowed) return <Blocked />;

  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <GAListener />
        <CustomerProvider>
          <AuthProvider>
            <OrderProvider>
              <PromotionProvider><CartProvider>
                <DeliverySettingsProvider>
                  <WishlistProvider>
                    <ComparisonProvider>
                    <SEOProvider>
                      <LoyaltySettingsProvider><AppContent /></LoyaltySettingsProvider>
                    </SEOProvider>
                    </ComparisonProvider>
                  </WishlistProvider>
                </DeliverySettingsProvider>
              </CartProvider></PromotionProvider>
            </OrderProvider>
          </AuthProvider>
        </CustomerProvider>
      </Router>
    </HelmetProvider>
  );
}
